/**
 * NPEContractFormDialog — New/Edit/Copy NPE Contract.
 *
 * Cấu trúc field & vị trí theo Access `frmPurchaseQryNPEAdd`
 * (frontend/plan/access_all/form_frmPurchaseQryNPEAdd.txt).
 *
 * Layout (sorted by Access Top — chỉ render các control có Visible=True trong Access):
 *   R1:  ID (display)                | NPE Contract No. (text, prefix NPE-)
 *   R2:  Supplier Code (combo cp_vn) | Contract Date (date)
 *   R3:  Supplier Name (auto, full row)
 *   R4:  Quality Code (combo)        | Certification (combo)
 *   R5:  Quantity (Kg)               | Delivery time
 *   R6:  Delivery Place (combo)      | —
 *   R7:  Delivery Place Name (auto, full row)
 *   R8:  Package Code (combo)        | —
 *   R9:  Package Name (auto, full row)
 *   R10: Shipper Code (combo cp_vn)  | —
 *   R11: Shipper Name (auto, full row)
 *   R12: Note (multiline, full row)
 *   R13: Interest Rate Note (full row)
 *   R14: Stoploss Rate (percent)     | Provisional on Market Price Rate (percent)
 *   R15: Trader In Charge (combo by full_name) | —
 *   R18: ── Section: Return Costs (*VND/KG/MONTH) ──
 *   R19: Storage Charge              | Insurance fee
 *   R20: Storage Loss                | —
 *   R21: ── Section: Return Costs (*VND/KG) ──
 *   R22: Two way handing fee         | —
 *   R23: Đã được TGĐ ký | Đã gửi KH | Đã nhận chữ ký KH (checkboxes)
 *
 * Ẩn theo Access (Visible = NotDefault = False), không render trong UI nhưng
 * state vẫn giữ trong form model để gửi/giữ giá trị khi Edit/Copy:
 *   - trader_in_charge (username, PIC)        — auto từ trader_in_charge_name
 *   - return_package / return_packaging_name
 *   - return_quality / return_quality_des
 *
 * Style/format theo `pages/saleContract/ContractFormDialog` (giữ nguyên).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DateTextField from '@/components/DateTextField'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateNPEContractMutation,
  useLazyCheckNPEContractNoExistsQuery,
  useLazySuggestNPEContractNoQuery,
  useUpdateNPEContractMutation,
} from '@/store/api/npeContractsApi'
import {
  useListCertificationsQuery,
  useListDeliveryPlacesQuery,
  useListPackagesQuery,
  useListQualitiesQuery,
  useListTradersQuery,
  useLookupVendorsQuery,
} from '@/store/api/lookupsApi'
import type { NPEContractCreateIn, NPEContractOut } from '@/types/api'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import { getErrorMessage } from '@/utils/error'

/* ------------------------------------------------------------------ */
/*  Style helpers — giữ nguyên                                          */
/* ------------------------------------------------------------------ */

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const readonlySx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
} as const

const toNum = (v: string): number | null => {
  if (!v) return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

const today = (): string => new Date().toISOString().slice(0, 10)

/* ------------------------------------------------------------------ */
/*  Form model                                                         */
/* ------------------------------------------------------------------ */

interface NPEContractFormModel {
  // ID (display only)
  consignmentPId: number | null

  // General
  contractNo: string
  contractDate: string
  cpCode: string
  supplierName: string // auto-fill (DLookup name from public_cp_vn)
  quantityKg: string
  qualityCode: string
  certification: string
  deliveryTime: string
  deliveryPlaceCode: string
  deliveryPlaceName: string // auto-fill
  packageCode: string
  packageName: string // auto-fill
  shipperCode: string
  shipperName: string // auto-fill
  note: string

  // Pricing
  interestRateNote: string
  stoplossRate: string
  provisionalMarketRate: string

  // Trader
  traderInCharge: string // username (PIC, auto-fill)
  traderInChargeName: string // full_name (selectable)

  // Return goods (returned quality/package descriptors)
  returnPackage: string
  returnQuality: string
  returnPackagingName: string // auto-fill
  returnQualityDes: string // auto-fill

  // Return Costs (*VND/KG/MONTH)
  storageCharge: string
  insuranceFee: string
  storageLoss: string

  // Return Costs (*VND/KG)
  twoWayHandlingFee: string

  // Workflow flags
  isSignedByGm: boolean
  isSentToClient: boolean
  isSignedByClient: boolean
}

const emptyForm = (): NPEContractFormModel => ({
  consignmentPId: null,
  contractNo: '',
  contractDate: '',
  cpCode: '',
  supplierName: '',
  quantityKg: '',
  qualityCode: '',
  certification: '',
  deliveryTime: '',
  deliveryPlaceCode: '',
  deliveryPlaceName: '',
  packageCode: '',
  packageName: '',
  shipperCode: '',
  shipperName: '',
  note: '',

  interestRateNote: '',
  stoplossRate: '',
  provisionalMarketRate: '',

  traderInCharge: '',
  traderInChargeName: '',

  returnPackage: '',
  returnQuality: '',
  returnPackagingName: '',
  returnQualityDes: '',

  storageCharge: '',
  insuranceFee: '',
  storageLoss: '',

  twoWayHandlingFee: '',

  isSignedByGm: false,
  isSentToClient: false,
  isSignedByClient: false,
})

// Defaults áp dụng khi New (theo Access btnNew_Click trong frmPurchaseMainSubFormNPE).
const newDefaults = (): NPEContractFormModel => ({
  ...emptyForm(),
  contractNo: 'NPE-',
  contractDate: today(),
  stoplossRate: '0.75',
  provisionalMarketRate: '0.7',
  storageCharge: '20',
  insuranceFee: '20',
  storageLoss: '50',
  twoWayHandlingFee: '130',
})

/* ------------------------------------------------------------------ */
/*  Hydration helpers (Out ⇄ form ⇄ API)                                */
/* ------------------------------------------------------------------ */

/**
 * `grantRecordValue` — port của `module_modCleanTextElement.Grant_Record_Value`.
 *
 * Access VBA gốc (xem `module_modCleanTextElement.txt`):
 *   - Mở `rst = OpenRecordset(strSQL)` trên `DOING_TABLE = consignment_p`.
 *   - Nếu `rst.EOF` → for each ctl in frm.Controls: `ctl.value = ""` (clear all).
 *   - Nếu có record → for each ctl: `ctl.value = rst.fields(ctl.name).value`
 *     (match-by-name; `On Error Resume Next` cho control không có column tương ứng).
 *
 * Hàm này được gọi bởi cả `btnEdit_Click` và `btnCopy_Click` trong Access form
 * `frmPurchaseMainSubFormNPE` (xem cùng tài liệu access_all). Port sang React:
 *   - `d = null/undefined` (EOF) → trả `emptyForm()` (clear toàn bộ field).
 *   - `d != null` → map từng raw column của `consignment_p` sang form field
 *     tương ứng (giữ đúng tên Access). Các display name (`supplierName`,
 *     `deliveryPlaceName`, `packageName`, `shipperName`, `traderInChargeName`,
 *     `returnPackagingName`, `returnQualityDes`) lấy nếu API có trả; nếu
 *     không, useEffect bên dưới sẽ DLookup từ lookups bằng code (mirror các
 *     control Access có `ControlSource = "=DLookup(...)"`).
 */
const grantRecordValue = (d: NPEContractOut | null | undefined): NPEContractFormModel => {
  if (!d) return emptyForm()
  return {
    // --- PK + business key + header ---
    consignmentPId: d.consignment_p_id ?? null,
    contractNo: d.contract_no ?? '',
    contractDate: d.contract_date ?? '',

    // --- Supplier (code + denormalized name) ---
    cpCode: d.cp_code ?? '',
    supplierName: d.name ?? d.short_name ?? d.supplier_name ?? '',

    // --- Quantity & quality ---
    quantityKg: d.quantity_kg != null ? String(d.quantity_kg) : '',
    qualityCode: d.quality_code ?? '',
    certification: d.certification ?? '',

    // --- Delivery (code + joined display) ---
    deliveryTime: d.delivery_time ?? '',
    deliveryPlaceCode: d.delivery_place_code ?? '',
    deliveryPlaceName: d.delivery_place ?? '',

    // --- Package (code + joined display) ---
    packageCode: d.package_code ?? '',
    packageName: d.package_name ?? '',

    // --- Shipper (code + joined display) ---
    shipperCode: d.shipper_code ?? '',
    shipperName: d.shipper_name ?? '',

    note: d.note ?? '',

    // --- Pricing ---
    interestRateNote: d.interest_rate_note ?? '',
    stoplossRate: d.stoploss_rate != null ? String(d.stoploss_rate) : '',
    provisionalMarketRate:
      d.provisional_market_rate != null ? String(d.provisional_market_rate) : '',

    // --- Trader (username raw + full_name display) ---
    traderInCharge: d.trader_in_charge ?? '',
    traderInChargeName: d.trader_in_charge_name ?? '',

    // --- Return goods (Access: Visible=False, vẫn hydrate state) ---
    returnPackage: d.return_package ?? '',
    returnQuality: d.return_quality ?? '',
    returnPackagingName: (d.return_packaging_name as string | undefined) ?? '',
    returnQualityDes: (d.return_quality_des as string | undefined) ?? '',

    // --- Return costs ---
    storageCharge: d.storage_charge != null ? String(d.storage_charge) : '',
    insuranceFee: d.insurance_fee != null ? String(d.insurance_fee) : '',
    storageLoss: d.storage_loss != null ? String(d.storage_loss) : '',
    twoWayHandlingFee:
      d.two_way_handling_fee != null ? String(d.two_way_handling_fee) : '',

    // --- Workflow flags ---
    isSignedByGm: !!d.is_signed_by_gm,
    isSentToClient: !!d.is_sent_to_client,
    isSignedByClient: !!d.is_signed_by_client,
  }
}

const formToApi = (f: NPEContractFormModel): NPEContractCreateIn => {
  const strOrNull = (v: string): string | null => {
    const t = v.trim()
    return t === '' ? null : t
  }
  return {
    contract_no: strOrNull(f.contractNo),
    contract_date: f.contractDate,
    cp_code: f.cpCode.trim(),
    quantity_kg: toNum(f.quantityKg) ?? 0,
    quality_code: strOrNull(f.qualityCode),
    certification: strOrNull(f.certification),
    delivery_time: strOrNull(f.deliveryTime),
    delivery_place_code: strOrNull(f.deliveryPlaceCode),
    package_code: strOrNull(f.packageCode),
    shipper_code: strOrNull(f.shipperCode),
    trader_in_charge: strOrNull(f.traderInCharge),
    note: strOrNull(f.note),

    stoploss_rate: toNum(f.stoplossRate),
    interest_rate: null,
    provisional_market_rate: toNum(f.provisionalMarketRate),
    return_quality: strOrNull(f.returnQuality),
    storage_charge: toNum(f.storageCharge),
    storage_loss: toNum(f.storageLoss),
    two_way_handling_fee: toNum(f.twoWayHandlingFee),
    insurance_fee: toNum(f.insuranceFee),
    interest_rate_note: strOrNull(f.interestRateNote),
    return_package: strOrNull(f.returnPackage),

    is_signed_by_gm: f.isSignedByGm,
    is_sent_to_client: f.isSentToClient,
    is_signed_by_client: f.isSignedByClient,
  }
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface NPEContractFormDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'copy'
  initialValue?: NPEContractOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function NPEContractFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: NPEContractFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'
  const isCopy = mode === 'copy'

  const [form, setForm] = useState<NPEContractFormModel>(emptyForm)
  type FormErrors = Partial<Record<keyof NPEContractFormModel, string>>
  const [errors, setErrors] = useState<FormErrors>({})

  const [createCt, { isLoading: creating }] = useCreateNPEContractMutation()
  const [updateCt, { isLoading: updating }] = useUpdateNPEContractMutation()
  const isBusy = creating || updating

  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' }, { skip: !open })
  const { data: qualities = [] } = useListQualitiesQuery(undefined, { skip: !open })
  const { data: certifications = [] } = useListCertificationsQuery(undefined, { skip: !open })
  const { data: places = [] } = useListDeliveryPlacesQuery(undefined, { skip: !open })
  const { data: packages = [] } = useListPackagesQuery(undefined, { skip: !open })
  const { data: traders = [] } = useListTradersQuery(undefined, { skip: !open })

  /* Pattern Futures Order (skill access-suggest): CHỈ dùng lazy hook để
   * tránh cache shared race giữa effect apply lần đầu và blur refetch. */
  const [triggerCheckNPENo] = useLazyCheckNPEContractNoExistsQuery()
  const [triggerSuggestNPENo, suggestNpeState] = useLazySuggestNPEContractNoQuery()
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, isEdit, initialValue?.contract_no])

  useEffect(() => {
    if (regenNotice && form.contractNo.trim() !== regenNotice.to) {
      setRegenNotice(null)
    }
  }, [form.contractNo, regenNotice])

  const handleContractNoBlur = async () => {
    if (isEdit) return
    const val = form.contractNo.trim()
    if (!val || val === 'NPE-') return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckNPENo({ contractNo: val }).unwrap()
      if (!check.exists) return
      const next = await triggerSuggestNPENo(undefined, false).unwrap()
      const newVal = (next.contractNo || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, contractNo: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  /* ----- Init on open -----
   *
   * Access btnEdit_Click / btnCopy_Click trong `frmPurchaseMainSubFormNPE` đều
   * gọi `Grant_Record_Value FORM_TMP_1, DOING_TABLE, strSQL` để hydrate raw
   * row vào form. Port React: dùng `grantRecordValue(initialValue)` (xem
   * docstring helper ở trên).
   *
   *   - Edit  → đặt form = grantRecordValue(initialValue) (như Access).
   *   - Copy  → grantRecordValue(initialValue) rồi override theo btnCopy:
   *             consignment_p_id = "", contract_no = Suggest, contract_date =
   *             Now(), reset 3 flag (giữ nguyên tất cả field nghiệp vụ).
   *   - New   → grantRecordValue(null) (EOF branch = clear) + newDefaults +
   *             contract_no/interest_rate_note từ API suggest.
   */
  useEffect(() => {
    if (!open) return
    setErrors({})
    if (isEdit && initialValue) {
      setForm(grantRecordValue(initialValue))
    } else if (isCopy && initialValue) {
      setForm({
        ...grantRecordValue(initialValue),
        consignmentPId: null,
        contractNo: 'NPE-',
        contractDate: today(),
        isSignedByGm: false,
        isSentToClient: false,
        isSignedByClient: false,
      })
    } else {
      setForm({
        ...grantRecordValue(null),
        ...newDefaults(),
        contractNo: 'NPE-',
        interestRateNote: '',
      })
    }
    // Skill access-suggest anti #4 (biến thể): KHÔNG đưa `suggestedNpeNo`
    // vào deps — blur trigger refetch suggest sẽ fire effect này lại và
    // reset form. Apply suggested qua effect riêng dưới.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, isCopy, initialValue])

  // Pattern Futures Order: lazy trigger suggest sau khi dialog mở (mode!=edit).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggestNPENo(undefined, false)
      .unwrap()
      .then((res) => {
        if (cancelled) return
        const newVal = (res.contractNo || '').trim()
        if (!newVal) return
        setForm((prev) => {
          if (prev.contractNo && prev.contractNo !== 'NPE-') return prev
          return {
            ...prev,
            contractNo: newVal,
            ...(isCopy ? {} : { interestRateNote: res.interestRateNote ?? '' }),
          }
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, isEdit, isCopy, triggerSuggestNPENo])

  const clearError = useCallback((key: keyof NPEContractFormModel) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const set = useCallback(
    <K extends keyof NPEContractFormModel>(key: K, value: NPEContractFormModel[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      clearError(key)
    },
    [clearError],
  )

  const setNum = useCallback(
    (key: keyof NPEContractFormModel, raw: string) => {
      setForm((prev) => ({ ...prev, [key]: raw.replace(/,/g, '') as never }))
      clearError(key)
    },
    [clearError],
  )

  /* ----- Derived display names: cập nhật theo code khi lookup tải xong ----- */
  useEffect(() => {
    if (!form.cpCode) return
    const v = vendors.find((x) => x.code === form.cpCode)
    if (v && !form.supplierName) setForm((p) => ({ ...p, supplierName: v.name ?? '' }))
  }, [form.cpCode, form.supplierName, vendors])

  useEffect(() => {
    if (!form.shipperCode) return
    const v = vendors.find((x) => x.code === form.shipperCode)
    if (v && !form.shipperName) setForm((p) => ({ ...p, shipperName: v.name ?? '' }))
  }, [form.shipperCode, form.shipperName, vendors])

  useEffect(() => {
    if (!form.deliveryPlaceCode) return
    const p = places.find((x) => x.value === form.deliveryPlaceCode)
    if (p && !form.deliveryPlaceName) setForm((prev) => ({ ...prev, deliveryPlaceName: p.label ?? '' }))
  }, [form.deliveryPlaceCode, form.deliveryPlaceName, places])

  useEffect(() => {
    if (!form.packageCode) return
    const p = packages.find((x) => x.value === form.packageCode)
    if (p && !form.packageName) setForm((prev) => ({ ...prev, packageName: p.label ?? '' }))
  }, [form.packageCode, form.packageName, packages])

  useEffect(() => {
    if (!form.traderInChargeName) return
    const t = traders.find((x) => x.full_name === form.traderInChargeName)
    if (t && !form.traderInCharge)
      setForm((prev) => ({ ...prev, traderInCharge: t.username ?? '' }))
  }, [form.traderInChargeName, form.traderInCharge, traders])

  // Return Package Name (auto-fill từ public_package)
  useEffect(() => {
    if (!form.returnPackage) return
    const p = packages.find((x) => x.value === form.returnPackage)
    if (p && !form.returnPackagingName)
      setForm((prev) => ({ ...prev, returnPackagingName: p.label ?? '' }))
  }, [form.returnPackage, form.returnPackagingName, packages])

  // Return Quality Des (auto-fill từ public_list_and_valo)
  useEffect(() => {
    if (!form.returnQuality) return
    const q = qualities.find((x) => x.code === form.returnQuality)
    if (q && !form.returnQualityDes)
      setForm((prev) => ({ ...prev, returnQualityDes: q.description ?? '' }))
  }, [form.returnQuality, form.returnQualityDes, qualities])

  /* ----- Validation (khớp Access btnSave_Click errCheck 1..10) ----- */
  const validateLikeAccess = (): FormErrors => {
    const errs: FormErrors = {}
    const cno = form.contractNo.trim()
    if (cno === '') {
      errs.contractNo = 'Please choose Contract No!'
    } else if (cno.slice(0, 4).toUpperCase() !== 'NPE-') {
      errs.contractNo = "Please type Consignment No. with the format 'NPE-*' !"
    }
    if (!form.contractDate) errs.contractDate = 'Please choose Contract Date!'
    if (!form.cpCode.trim()) errs.cpCode = 'Please choose Supplier Code!'

    const qty = toNum(form.quantityKg)
    if (form.quantityKg === '' || qty == null) {
      errs.quantityKg = 'Please type Quantity Kg!'
    } else if (qty <= 0) {
      errs.quantityKg = 'Quantity Kg must be > 0!'
    }

    if (!form.qualityCode.trim()) errs.qualityCode = 'Please choose Quality Code!'
    if (!form.deliveryTime) {
      errs.deliveryTime = 'Please choose Delivery Time!'
    } else if (form.contractDate && form.deliveryTime < form.contractDate) {
      errs.deliveryTime = 'Delivery Time must be >= Contract Date!'
    }
    if (!form.deliveryPlaceCode.trim()) errs.deliveryPlaceCode = 'Please choose Delivery Place!'
    if (!form.traderInCharge.trim()) errs.traderInChargeName = 'Please choose Trader In Charge!'
    return errs
  }

  /* ----- Save ----- */
  const handleSave = async () => {
    if (precheckBusyRef.current) return
    const errs = validateLikeAccess()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      snack.error('Please check the highlighted fields!')
      // Access btnSave errCheck=9 side effect: khi prefix sai, reset contract_no
      // về "NPE-" để user gõ lại phần đuôi (giữ parity với form Access).
      const cno = form.contractNo.trim()
      if (cno !== '' && cno.slice(0, 4).toUpperCase() !== 'NPE-') {
        setForm((prev) => ({ ...prev, contractNo: 'NPE-' }))
      }
      return
    }
    setErrors({})
    const body = formToApi(form)
    try {
      if (isEdit && initialValue) {
        const res = await updateCt({ contract_no: initialValue.contract_no, body }).unwrap()
        snack.success(`Successfully Updated`)
        onSaved?.(res.data?.consignment_p_id ?? initialValue.consignment_p_id)
      } else {
        const res = await createCt(body).unwrap()
        snack.success('Successfully Added')
        onSaved?.(res.data?.consignment_p_id ?? 0)
      }
      onClose()
    } catch (e) {
      const errMsg = getErrorMessage(e, isEdit ? 'Update failed' : 'Failed to create NPE Contract')
      // Skill access-suggest anti #13: BE raise duplicate → tự suggest mã mới.
      // BE messages đã chuẩn hoá English ("already exists" / "duplicate").
      if (!isEdit && /exist|duplicate/i.test(errMsg)) {
        try {
          const next = await triggerSuggestNPENo(undefined, false).unwrap()
          const newVal = (next.contractNo || '').trim()
          const oldVal = form.contractNo.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, contractNo: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch { /* fallthrough */ }
      }
      snack.error(errMsg)
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setErrors({})
    onClose()
  }

  /* ----- Vendor autocomplete options -----
   *
   * Fallback synthetic vendor (theo pattern LocalP) khi `vendors` lookup chưa
   * load xong hoặc khi mã đã bị xoá khỏi `public_cp_vn`. Tránh Autocomplete
   * render trống dù form state đã có cp_code/shipper_code + supplier_name. */
  const cpVendorValue = useMemo(
    () =>
      vendors.find((v) => v.code === form.cpCode)
      ?? (form.cpCode
            ? { code: form.cpCode, name: form.supplierName ?? '', short_name: null, vat_code: null }
            : null),
    [vendors, form.cpCode, form.supplierName],
  )
  const shipperVendorValue = useMemo(
    () =>
      vendors.find((v) => v.code === form.shipperCode)
      ?? (form.shipperCode
            ? { code: form.shipperCode, name: form.shipperName ?? '', short_name: null, vat_code: null }
            : null),
    [vendors, form.shipperCode, form.shipperName],
  )

  const dialogTitle = useMemo(() => {
    if (isEdit) return `Purchase - NPE Contract — ${initialValue?.contract_no ?? ''}`
    if (isCopy) return 'Purchase - NPE Contract (Copy)'
    return 'Purchase - NPE Contract'
  }, [isEdit, isCopy, initialValue])

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={dialogTitle}
          sx={{
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Purchase - </Box>
          {isEdit ? `NPE Contract — ${initialValue?.contract_no ?? ''}` : isCopy ? 'NPE Contract (Copy)' : 'NPE Contract'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={isBusy || !canEdit}
            sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
          >
            {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiInputLabel-shrink': { fontWeight: 600 },
          '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiCheckbox-root': { p: 0.5 },
          '& .MuiSvgIcon-root': { fontSize: '1rem' },
          '& .MuiTypography-caption': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          },
          '& .MuiTypography-body2': { fontSize: '0.8125rem' },
          '& .MuiSelect-select': { fontSize: '0.8125rem' },
        }}
      >
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            NPE Contract No. <b>{regenNotice.from}</b> was just created —
            suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            {/* R1: ID | NPE Contract No. ---------------------------------- */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                ID
              </Typography>
              <Typography variant="body2" sx={{ minHeight: 32, lineHeight: '32px', color: 'text.secondary' }}>
                {form.consignmentPId != null ? form.consignmentPId : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="NPE Contract No."
                size="small"
                fullWidth
                value={form.contractNo}
                onChange={(e) => set('contractNo', e.target.value.toUpperCase())}
                onBlur={() => void handleContractNoBlur()}
                InputProps={{ readOnly: isEdit }}
                placeholder={isEdit ? '' : 'NPE-XXXXX'}
                inputProps={{ tabIndex: 1 }}
                sx={isEdit ? readonlySx : undefined}
                error={!!errors.contractNo}
                helperText={
                  errors.contractNo ??
                  (precheckBusy
                    ? 'Verifying contract code…'
                    : suggestNpeState.isFetching
                      ? 'Suggesting next code…'
                      : undefined)
                }
              />
            </Grid>

            {/* R2: Supplier Code | Contract Date ------------------------- */}
            <Grid item xs={12} sm={6}>
              {/* inject sel vào options → tắt MUI "none of options match" (anti-pattern #46) */}
              <Autocomplete
                size="small"
                options={cpVendorValue && !vendors.some((o) => o.code === cpVendorValue.code) ? [cpVendorValue, ...vendors] : vendors}
                value={cpVendorValue}
                onChange={(_e, v) => {
                  set('cpCode', v?.code ?? '')
                  set('supplierName', v?.name ?? '')
                }}
                getOptionLabel={(o) => (o ? `${o.code} — ${o.name ?? ''}` : '')}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier Code"
                    inputProps={{ ...params.inputProps, tabIndex: 2 }}
                    error={!!errors.cpCode}
                    helperText={errors.cpCode}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Contract Date"
                size="small"
                fullWidth
                value={form.contractDate}
                onChange={(v) => set('contractDate', v)}
                nativeInputProps={{ tabIndex: 3 }}
                error={!!errors.contractDate}
                helperText={errors.contractDate}
              />
            </Grid>

            {/* R3: Supplier Name (full row, auto) ------------------------ */}
            <Grid item xs={12}>
              <TextField
                label="Supplier Name"
                size="small"
                fullWidth
                value={form.supplierName}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: 4 }}
                sx={readonlySx}
              />
            </Grid>

            {/* R4: Quality Code | Certification -------------------------
                sel ảo cho stored value chưa có trong lookup (lookup chưa load
                / option bị soft-delete) — Autocomplete hiển thị đúng giá trị
                Edit/Copy ngay khi mở form. */}
            <Grid item xs={12} sm={6}>
              {(() => {
                const sel = qualities.find((q) => q.code === form.qualityCode)
                  ?? (form.qualityCode ? { code: form.qualityCode, description: '' } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const qualityOpts = sel && !qualities.some((o) => o.code === sel.code) ? [sel, ...qualities] : qualities
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={qualityOpts}
                    value={sel}
                    onChange={(_e, v) => set('qualityCode', v?.code ?? '')}
                    getOptionLabel={(o) => o ? `${o.code} — ${o.description ?? ''}` : ''}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderInput={(params) => (
                      <TextField {...params} label="Quality Code"
                        inputProps={{ ...params.inputProps, tabIndex: 5 }}
                        error={!!errors.qualityCode} helperText={errors.qualityCode} />
                    )}
                  />
                )
              })()}
            </Grid>
            <Grid item xs={12} sm={6}>
              {(() => {
                const sel = certifications.find((c) => c.certification === form.certification)
                  ?? (form.certification ? { certification: form.certification } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const certificationOpts = sel && !certifications.some((o) => o.certification === sel.certification) ? [sel, ...certifications] : certifications
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={certificationOpts}
                    value={sel}
                    onChange={(_e, v) => set('certification', v?.certification ?? '')}
                    getOptionLabel={(o) => o ? o.certification : ''}
                    isOptionEqualToValue={(a, b) => a.certification === b.certification}
                    renderInput={(params) => (
                      <TextField {...params} label="Certification"
                        inputProps={{ ...params.inputProps, tabIndex: 6 }}
                        error={!!errors.certification} helperText={errors.certification} />
                    )}
                  />
                )
              })()}
            </Grid>

            {/* R5: Quantity (Kg) | Delivery time ------------------------ */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity (Kg)"
                size="small"
                fullWidth
                value={fmtNumStr(form.quantityKg)}
                onChange={(e) => setNum('quantityKg', e.target.value)}
                inputProps={{ tabIndex: 7, inputMode: 'decimal' }}
                sx={numSx}
                error={!!errors.quantityKg}
                helperText={errors.quantityKg}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Delivery time"
                size="small"
                fullWidth
                value={form.deliveryTime}
                onChange={(v) => set('deliveryTime', v)}
                nativeInputProps={{ tabIndex: 8 }}
                error={!!errors.deliveryTime}
                helperText={errors.deliveryTime}
              />
            </Grid>

            {/* R6: Delivery Place | — ----------------------------------- */}
            <Grid item xs={12} sm={6}>
              {(() => {
                const sel = places.find((p) => p.value === form.deliveryPlaceCode)
                  ?? (form.deliveryPlaceCode ? { value: form.deliveryPlaceCode, label: '' } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const placeOpts = sel && !places.some((o) => o.value === sel.value) ? [sel, ...places] : places
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={placeOpts}
                    value={sel}
                    onChange={(_e, v) => {
                      set('deliveryPlaceCode', v?.value ?? '')
                      set('deliveryPlaceName', v?.label ?? '')
                    }}
                    getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => (
                      <TextField {...params} label="Delivery Place"
                        inputProps={{ ...params.inputProps, tabIndex: 9 }}
                        error={!!errors.deliveryPlaceCode} helperText={errors.deliveryPlaceCode} />
                    )}
                  />
                )
              })()}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R7: Delivery Place Name (full row, auto) ----------------- */}
            <Grid item xs={12}>
              <TextField
                label="Delivery Place Name"
                size="small"
                fullWidth
                value={form.deliveryPlaceName}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: 10 }}
                sx={readonlySx}
              />
            </Grid>

            {/* R8: Package Code | — ------------------------------------- */}
            <Grid item xs={12} sm={6}>
              {(() => {
                const sel = packages.find((p) => p.value === form.packageCode)
                  ?? (form.packageCode ? { value: form.packageCode, label: '' } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const packageOpts = sel && !packages.some((o) => o.value === sel.value) ? [sel, ...packages] : packages
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={packageOpts}
                    value={sel}
                    onChange={(_e, v) => {
                      set('packageCode', v?.value ?? '')
                      set('packageName', v?.label ?? '')
                    }}
                    getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => (
                      <TextField {...params} label="Package Code"
                        inputProps={{ ...params.inputProps, tabIndex: 11 }}
                        error={!!errors.packageCode} helperText={errors.packageCode} />
                    )}
                  />
                )
              })()}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R9: Package Name (full row, auto) ------------------------ */}
            <Grid item xs={12}>
              <TextField
                label="Package Name"
                size="small"
                fullWidth
                value={form.packageName}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: 12 }}
                sx={readonlySx}
              />
            </Grid>

            {/* R10: Shipper Code | — ------------------------------------ */}
            <Grid item xs={12} sm={6}>
              {/* inject sel vào options → tắt MUI "none of options match" (anti-pattern #46) */}
              <Autocomplete
                size="small"
                options={shipperVendorValue && !vendors.some((o) => o.code === shipperVendorValue.code) ? [shipperVendorValue, ...vendors] : vendors}
                value={shipperVendorValue}
                onChange={(_e, v) => {
                  set('shipperCode', v?.code ?? '')
                  set('shipperName', v?.name ?? '')
                }}
                getOptionLabel={(o) => (o ? `${o.code} — ${o.name ?? ''}` : '')}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField {...params} label="Shipper Code" inputProps={{ ...params.inputProps, tabIndex: 13 }} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R11: Shipper Name (full row, auto) ----------------------- */}
            <Grid item xs={12}>
              <TextField
                label="Shipper Name"
                size="small"
                fullWidth
                value={form.shipperName}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: 14 }}
                sx={readonlySx}
              />
            </Grid>

            {/* R12: Note (full row, multiline) -------------------------- */}
            <Grid item xs={12}>
              <TextField
                label="Note"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={form.note}
                onChange={(e) => set('note', e.target.value)}
                inputProps={{ tabIndex: 15 }}
              />
            </Grid>

            {/* R13: Interest Rate Note (full row) ----------------------- */}
            <Grid item xs={12}>
              <TextField
                label="Interest Rate Note"
                size="small"
                fullWidth
                value={form.interestRateNote}
                onChange={(e) => set('interestRateNote', e.target.value)}
                inputProps={{ tabIndex: 16 }}
              />
            </Grid>

            {/* R14: Stoploss Rate | Provisional on Market Price Rate ---- */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Stoploss Rate"
                size="small"
                fullWidth
                value={fmtNumStr(form.stoplossRate)}
                onChange={(e) => setNum('stoplossRate', e.target.value)}
                inputProps={{ tabIndex: 17, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Provisional on Market Price Rate"
                size="small"
                fullWidth
                value={fmtNumStr(form.provisionalMarketRate)}
                onChange={(e) => setNum('provisionalMarketRate', e.target.value)}
                inputProps={{ tabIndex: 18, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>

            {/* R15: Trader In Charge (combo by full_name) — PIC ẩn theo Access (Visible=False). */}
            <Grid item xs={12} sm={6}>
              {(() => {
                const sel = traders.find((t) => t.full_name === form.traderInChargeName)
                  ?? (form.traderInChargeName ? { username: '', full_name: form.traderInChargeName } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const traderOpts = sel && !traders.some((o) => o.full_name === sel.full_name) ? [sel, ...traders] : traders
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={traderOpts}
                    value={sel}
                    onChange={(_e, v) => {
                      set('traderInChargeName', v?.full_name ?? '')
                      set('traderInCharge', v?.username ?? '')
                    }}
                    getOptionLabel={(o) => o ? `${o.username ?? ''}${o.username ? ' — ' : ''}${o.full_name}` : ''}
                    isOptionEqualToValue={(a, b) => a.full_name === b.full_name}
                    renderInput={(params) => (
                      <TextField {...params} label="Trader In Charge"
                        inputProps={{ ...params.inputProps, tabIndex: 19 }}
                        error={!!errors.traderInChargeName} helperText={errors.traderInChargeName} />
                    )}
                  />
                )
              })()}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R16/R17 (Return Package, Return Quality, Return Package Name,
                Return Quality Des) ẩn theo Access (Visible = NotDefault = False).
                State vẫn giữ trong form model để giữ giá trị khi Edit/Copy. */}

            {/* R18: Section ── Return Costs (*VND/KG/MONTH) ── ---------- */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Return Costs (*VND/KG/MONTH)
                </Typography>
              </Box>
            </Grid>

            {/* R19: Storage Charge | Insurance fee ---------------------- */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Storage Charge"
                size="small"
                fullWidth
                value={fmtNumStr(form.storageCharge)}
                onChange={(e) => setNum('storageCharge', e.target.value)}
                inputProps={{ tabIndex: 25, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Insurance fee"
                size="small"
                fullWidth
                value={fmtNumStr(form.insuranceFee)}
                onChange={(e) => setNum('insuranceFee', e.target.value)}
                inputProps={{ tabIndex: 26, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>

            {/* R20: Storage Loss | — ------------------------------------ */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Storage Loss"
                size="small"
                fullWidth
                value={fmtNumStr(form.storageLoss)}
                onChange={(e) => setNum('storageLoss', e.target.value)}
                inputProps={{ tabIndex: 27, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R21: Section ── Return Costs (*VND/KG) ── ---------------- */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Return Costs (*VND/KG)
                </Typography>
              </Box>
            </Grid>

            {/* R22: Two way handing fee | — ----------------------------- */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Two way handing fee"
                size="small"
                fullWidth
                value={fmtNumStr(form.twoWayHandlingFee)}
                onChange={(e) => setNum('twoWayHandlingFee', e.target.value)}
                inputProps={{ tabIndex: 28, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            {/* R23: Workflow flags (3 checkboxes) ----------------------- */}
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.isSignedByGm}
                    tabIndex={29}
                    onChange={(e) => set('isSignedByGm', e.target.checked)}
                  />
                }
                label="Signed by GM"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.isSentToClient}
                    tabIndex={30}
                    onChange={(e) => set('isSentToClient', e.target.checked)}
                  />
                }
                label="Sent to Client"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={form.isSignedByClient}
                    tabIndex={31}
                    onChange={(e) => set('isSignedByClient', e.target.checked)}
                  />
                }
                label="Signed by Client"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default NPEContractFormDialog
